import {Divider, Form} from 'antd';
import {FormInstance} from 'antd/es/form';
import {FormListFieldData} from 'antd/es/form/FormList';
import {isMatch} from 'lodash-es';
import React, {useEffect, FC} from 'react';

import {IProgramArgsType, IProgramSetProgramsType} from 'components/form/fields/fields-tree/fields-tree-types';
import {IFieldMeta} from 'entities/metadata';
import {IFormEntityData} from 'modules/data/data-types';

import {FieldsTreeGenerator} from '../fields-tree-generator';

import '../fields-tree.less';

interface IFieldsTreeChildProps {
    fieldMeta: IFieldMeta;
    entityName: string;
    parentField: FormListFieldData;
    formData?: IFormEntityData;
    form?: FormInstance;
}

export const FieldTreeChild: FC<IFieldsTreeChildProps> = ({
    fieldMeta,
    form,
    formData,
    parentField,
    entityName,
}: IFieldsTreeChildProps) => {
    const {programSetPrograms} = form?.getFieldsValue() || {};

    useEffect(() => {
        if (!parentField) return;
        const programSet = programSetPrograms[parentField.name];
        const programSetForUpdate = JSON.parse(JSON.stringify(programSetPrograms));
        let programArgs: IProgramArgsType[] | undefined = [];

        if (programSet && Array.isArray(formData?.data?.programSetPrograms)
            && formData?.data?.programSetPrograms[parentField.name]) {
            programArgs = (
                formData?.data?.programSetPrograms[parentField.name] as unknown as IProgramSetProgramsType
            )?.programArgs;
        }

        const defaultProgramSet: IProgramArgsType[] = [
            {
                code: '',
                defaultType: undefined,
            },
        ];
        if (programSetPrograms && programSet && Array.isArray(programArgs)) {
            if (!fieldMeta.children) return;
            if (!programSet || !parentField) return;

            // setting default programs for empty programs
            if (programArgs.length === 0) {
                if (!isMatch(programSet.programSetArgs, defaultProgramSet)) {
                    programSetForUpdate[parentField.name].programSetArgs = defaultProgramSet;
                    form?.setFieldsValue({programSetPrograms: programSetForUpdate});
                }
                return;
            }

            const argKeys = fieldMeta.children.map(child => child.key);
            const newArgs: IProgramArgsType[] = [];
            const newValues = form?.getFieldsValue();
            programArgs.forEach((arg: any) => {
                const newArg = defaultProgramSet[0];
                argKeys.forEach((key: string) => {
                    newArg[key] = arg[key] || '';
                });
                newArgs.push(JSON.parse(JSON.stringify(newArg)));
            });

            if (newValues.programSetPrograms && newValues.programSetPrograms[parentField.name]) {
                if (Array.isArray(newArgs) && Array.isArray(programSet?.programSetArgs)
                    && newArgs.length === programSet?.programSetArgs.length
                    && !programSet?.programSetArgs.some((arg: IProgramArgsType, index: number) => (
                        arg.code !== newArgs[index].code
                    ))) return;
                if (isMatch(newArgs, programSetPrograms[parentField.name]?.programSetArgs)) return;

                programSetForUpdate[parentField.name].programSetArgs = newArgs;

                // filter empty programs
                newValues.programSetPrograms = programSetForUpdate.filter(
                    (
                        programSetData: IProgramSetProgramsType,
                    ) => programSetData.program && 'id' in programSetData.program,
                );

                if (!isMatch(
                    programSetForUpdate[parentField.name]?.programSetArgs,
                    form?.getFieldsValue().programSetPrograms[parentField.name]?.programSetArgs,
                )) {
                    form?.setFieldsValue(newValues);
                }
            }
        }
    }, [programSetPrograms]);

    return (
        <div className="fields-tree">
            <Form.List
                {...parentField}
                name={[parentField.name, fieldMeta.key]}
            >
                {(fields: FormListFieldData[]) => (
                    <div>
                        <div className="fields-tree__header">{fieldMeta.label}</div>
                        <div className="fields-tree__wrapper">
                            {((!fields.length || fields.length === 1) ? [{fieldKey: 0, key: 0, name: 0}] : fields)
                                ?.map((childField: FormListFieldData, index) => (
                                    <div key={childField.key}>
                                        <Form.Item
                                            className="row"
                                            {...childField}
                                            name={[childField.name, fieldMeta.key]}
                                        >
                                            <FieldsTreeGenerator
                                                fieldData={childField}
                                                meta={fieldMeta}
                                                form={form}
                                                formData={formData}
                                                entityName={entityName}
                                            />
                                        </Form.Item>
                                        {index < fields.length - 1 && <Divider />}
                                    </div>
                                ))}
                        </div>
                    </div>
                )}
            </Form.List>
        </div>
    );
};
